/**
 * Guided flow registry — sub-flows within Parcours entries.
 * Domain owns IDs, ordering, prerequisites, org completion rules.
 * Admin owns FR labels, routes, tour steps.
 */
import type { CapabilityPack } from '../identity/capability-packs.js';
import { CAPABILITY_PACK, packsForRoles, sodViolations } from '../identity/capability-packs.js';
import { GUIDED_FLOWS_ENABLED } from '../identity/admin-surface.js';
import {
  machineStepComplete,
  packBlockedReason,
  packPrerequisitesMet,
  type MachineJourneyId,
  type MachineReadinessSignals,
} from './machine-journey.js';
import { isTeamCompositionSlotsMet } from './platform-setup.js';

export type GuidedFlowAvailability = 'ready' | 'partial' | 'unavailable';

export type GuidedFlowOrgStatus = 'complete' | 'actionable' | 'blocked' | 'unavailable';

export type GuidedFlowDefinition = {
  id: string;
  revision: number;
  entryId: MachineJourneyId;
  order: number;
  requiredPacks: readonly CapabilityPack[];
  prerequisites: readonly string[];
  operationalGoal: string;
  principleIds: readonly number[];
  availability: GuidedFlowAvailability;
};

export type PeopleOrgSignals = {
  activeStaffCount: number;
  activeStaffWithRolesCount: number;
  superAdminCount: number;
  /** At least one active account covering each operational pole in use. */
  poleCoverage: {
    catalog: boolean;
    buy: boolean;
    warehouse: boolean;
    sales: boolean;
    finance: boolean;
  };
  /**
   * Per-account role name lists (active staff). When set, team composition /
   * people-cover-poles use SETUP_TEAM_COMPOSITION instead of boolean poles alone.
   */
  accountsRoleNames?: readonly (readonly string[])[];
  reportingGapCount: number;
  sodViolationAccountCount: number;
};

export type FlowReadinessContext = {
  signals: MachineReadinessSignals;
  people?: PeopleOrgSignals;
};

export type GuidedFlowState = {
  id: string;
  status: GuidedFlowOrgStatus;
  blockedBy: string | null;
  evidence: Record<string, string | number | boolean>;
};

export const GUIDED_FLOWS: readonly GuidedFlowDefinition[] = [
  // People
  {
    id: 'people-learn-roles',
    revision: 1,
    entryId: 'people',
    order: 0,
    requiredPacks: [CAPABILITY_PACK.PARTY_ADMIN],
    prerequisites: [],
    operationalGoal: 'Comprendre ce que chaque rôle peut faire.',
    principleIds: [6],
    availability: 'ready',
  },
  {
    id: 'people-create-account',
    revision: 1,
    entryId: 'people',
    order: 1,
    requiredPacks: [CAPABILITY_PACK.PARTY_ADMIN],
    prerequisites: ['people-learn-roles'],
    operationalGoal: 'Créer un compte par personne.',
    principleIds: [6],
    availability: 'ready',
  },
  {
    id: 'people-cover-poles',
    revision: 1,
    entryId: 'people',
    order: 2,
    requiredPacks: [CAPABILITY_PACK.PARTY_ADMIN],
    prerequisites: ['people-create-account'],
    operationalGoal: 'Couvrir les pôles opérationnels actifs.',
    principleIds: [6],
    availability: 'ready',
  },
  {
    id: 'people-verify-authority',
    revision: 1,
    entryId: 'people',
    order: 3,
    requiredPacks: [CAPABILITY_PACK.PARTY_ADMIN],
    prerequisites: ['people-cover-poles'],
    operationalGoal: 'Vérifier super-admin, rôles et reporting.',
    principleIds: [6],
    availability: 'ready',
  },
  {
    id: 'people-verify-sod',
    revision: 1,
    entryId: 'people',
    order: 4,
    requiredPacks: [CAPABILITY_PACK.PARTY_ADMIN],
    prerequisites: ['people-verify-authority'],
    operationalGoal: 'Aucun cumul de droits interdit.',
    principleIds: [6],
    availability: 'ready',
  },
  // Catalog
  {
    id: 'catalog-create-product',
    revision: 1,
    entryId: 'catalog',
    order: 0,
    requiredPacks: [CAPABILITY_PACK.CATALOG_MAINTAIN],
    prerequisites: [],
    operationalGoal: 'Créer un produit identifiable.',
    principleIds: [2],
    availability: 'ready',
  },
  {
    id: 'catalog-create-variant',
    revision: 1,
    entryId: 'catalog',
    order: 1,
    requiredPacks: [CAPABILITY_PACK.CATALOG_MAINTAIN],
    prerequisites: ['catalog-create-product'],
    operationalGoal: 'Ajouter une variante stable.',
    principleIds: [2],
    availability: 'ready',
  },
  {
    id: 'catalog-readiness',
    revision: 1,
    entryId: 'catalog',
    order: 2,
    requiredPacks: [CAPABILITY_PACK.CATALOG_MAINTAIN],
    prerequisites: ['catalog-create-variant'],
    operationalGoal: 'Résoudre les manques avant publication.',
    principleIds: [2],
    availability: 'partial',
  },
  // Locations
  {
    id: 'locations-create',
    revision: 1,
    entryId: 'locations',
    order: 0,
    requiredPacks: [CAPABILITY_PACK.STOCK_MOVE],
    prerequisites: [],
    operationalGoal: 'Créer un emplacement entrepôt.',
    principleIds: [3],
    availability: 'ready',
  },
  {
    id: 'locations-classes',
    revision: 1,
    entryId: 'locations',
    order: 1,
    requiredPacks: [CAPABILITY_PACK.STOCK_MOVE],
    prerequisites: ['locations-create'],
    operationalGoal: 'Comprendre les classes de stock.',
    principleIds: [3],
    availability: 'ready',
  },
  // Buy
  {
    id: 'buy-qualify-supplier',
    revision: 1,
    entryId: 'buy',
    order: 0,
    requiredPacks: [CAPABILITY_PACK.BUY_PROCURE],
    prerequisites: [],
    operationalGoal: 'Qualifier un fournisseur.',
    principleIds: [4],
    availability: 'ready',
  },
  {
    id: 'buy-create-po',
    revision: 1,
    entryId: 'buy',
    order: 1,
    requiredPacks: [CAPABILITY_PACK.BUY_PROCURE],
    prerequisites: ['buy-qualify-supplier'],
    operationalGoal: 'Créer et soumettre un bon de commande.',
    principleIds: [4],
    availability: 'ready',
  },
  // Receive
  {
    id: 'receive-po',
    revision: 1,
    entryId: 'receive',
    order: 0,
    requiredPacks: [CAPABILITY_PACK.STOCK_MOVE],
    prerequisites: [],
    operationalGoal: 'Réceptionner contre un BC.',
    principleIds: [3],
    availability: 'ready',
  },
  {
    id: 'receive-verify-lot',
    revision: 1,
    entryId: 'receive',
    order: 1,
    requiredPacks: [CAPABILITY_PACK.STOCK_MOVE],
    prerequisites: ['receive-po'],
    operationalGoal: 'Vérifier lot en quarantaine.',
    principleIds: [3],
    availability: 'ready',
  },
  // Release
  {
    id: 'release-lot',
    revision: 1,
    entryId: 'release',
    order: 0,
    requiredPacks: [CAPABILITY_PACK.STOCK_MOVE],
    prerequisites: [],
    operationalGoal: 'Libérer un lot contrôlé.',
    principleIds: [3],
    availability: 'ready',
  },
  {
    id: 'release-verify-atp',
    revision: 1,
    entryId: 'release',
    order: 1,
    requiredPacks: [CAPABILITY_PACK.STOCK_MOVE],
    prerequisites: ['release-lot'],
    operationalGoal: 'Vérifier la quantité commandable.',
    principleIds: [3, 7],
    availability: 'ready',
  },
  // Sell
  {
    id: 'sell-create-order',
    revision: 1,
    entryId: 'sell',
    order: 0,
    requiredPacks: [CAPABILITY_PACK.SELL_COMMIT],
    prerequisites: [],
    operationalGoal: 'Créer une commande client.',
    principleIds: [5],
    availability: 'ready',
  },
  {
    id: 'sell-confirm-ship',
    revision: 1,
    entryId: 'sell',
    order: 1,
    requiredPacks: [CAPABILITY_PACK.SELL_COMMIT],
    prerequisites: ['sell-create-order'],
    operationalGoal: 'Confirmer, réserver et expédier.',
    principleIds: [5],
    availability: 'ready',
  },
  // Return
  {
    id: 'return-order',
    revision: 1,
    entryId: 'return',
    order: 0,
    requiredPacks: [CAPABILITY_PACK.STOCK_MOVE],
    prerequisites: [],
    operationalGoal: 'Enregistrer un retour commande.',
    principleIds: [5],
    availability: 'ready',
  },
  {
    id: 'return-assess-or-stop',
    revision: 1,
    entryId: 'return',
    order: 1,
    requiredPacks: [CAPABILITY_PACK.STOCK_MOVE],
    prerequisites: ['return-order'],
    operationalGoal: 'Évaluer retour ou arrêter un lot.',
    principleIds: [5],
    availability: 'ready',
  },
  // Money
  {
    id: 'money-inspect-coa',
    revision: 1,
    entryId: 'money',
    order: 0,
    requiredPacks: [CAPABILITY_PACK.MONEY_READ],
    prerequisites: [],
    operationalGoal: 'Inspecter le plan comptable.',
    principleIds: [1],
    availability: 'unavailable',
  },
  {
    id: 'money-close-period',
    revision: 1,
    entryId: 'money',
    order: 1,
    requiredPacks: [CAPABILITY_PACK.MONEY_POST],
    prerequisites: ['money-inspect-coa'],
    operationalGoal: 'Clôturer une période sur écritures réelles.',
    principleIds: [1],
    availability: 'unavailable',
  },
] as const;

export type GuidedFlowId = (typeof GUIDED_FLOWS)[number]['id'];

const FLOW_BY_ID = new Map(GUIDED_FLOWS.map((f) => [f.id, f]));

export function guidedFlowById(id: string): GuidedFlowDefinition | undefined {
  return FLOW_BY_ID.get(id);
}

export function flowsForEntry(entryId: MachineJourneyId): GuidedFlowDefinition[] {
  return GUIDED_FLOWS.filter((f) => f.entryId === entryId).sort((a, b) => a.order - b.order);
}

export function visibleFlowIds(
  userPacks: ReadonlySet<CapabilityPack>,
  opts?: { isSuperAdmin?: boolean },
): string[] {
  if (!GUIDED_FLOWS_ENABLED) return [];
  if (opts?.isSuperAdmin) {
    return GUIDED_FLOWS.filter((f) => f.availability !== 'unavailable').map((f) => f.id);
  }
  return GUIDED_FLOWS.filter((f) => {
    if (f.availability === 'unavailable') return false;
    return f.requiredPacks.some((p) => userPacks.has(p));
  }).map((f) => f.id);
}

/** Validate registry integrity — for tests and startup checks. */
export function validateGuidedFlowRegistry(): string[] {
  const errors: string[] = [];
  const ids = new Set<string>();
  for (const flow of GUIDED_FLOWS) {
    if (ids.has(flow.id)) errors.push(`duplicate flow id: ${flow.id}`);
    ids.add(flow.id);
    for (const pre of flow.prerequisites) {
      if (!FLOW_BY_ID.has(pre)) errors.push(`missing prerequisite ${pre} for ${flow.id}`);
    }
  }
  // Cycle detection (DFS)
  const visiting = new Set<string>();
  const visited = new Set<string>();
  function dfs(id: string): boolean {
    if (visiting.has(id)) return true;
    if (visited.has(id)) return false;
    visiting.add(id);
    const flow = FLOW_BY_ID.get(id);
    if (flow) {
      for (const pre of flow.prerequisites) {
        if (dfs(pre)) return true;
      }
    }
    visiting.delete(id);
    visited.add(id);
    return false;
  }
  for (const flow of GUIDED_FLOWS) {
    if (dfs(flow.id)) {
      errors.push(`cycle detected involving ${flow.id}`);
      break;
    }
  }
  return errors;
}

function entryPackBlocked(entryId: MachineJourneyId, ctx: FlowReadinessContext): string | null {
  if (!packPrerequisitesMet(entryId, ctx.signals)) {
    const upstream = packBlockedReason(entryId, ctx.signals);
    if (upstream) {
      const upstreamFlows = flowsForEntry(upstream);
      const last = upstreamFlows[upstreamFlows.length - 1];
      return last?.id ?? upstream;
    }
  }
  return null;
}

function flowPrerequisitesMet(flowId: string, completedFlowIds: ReadonlySet<string>): boolean {
  const flow = FLOW_BY_ID.get(flowId);
  if (!flow) return false;
  return flow.prerequisites.every((p) => completedFlowIds.has(p));
}

function peoplePolesCovered(people: PeopleOrgSignals): boolean {
  const { poleCoverage } = people;
  const poles = [poleCoverage.catalog, poleCoverage.buy, poleCoverage.warehouse, poleCoverage.sales, poleCoverage.finance];
  return poles.every(Boolean);
}

/** Prefer explicit accounts when present; else legacy boolean poles. */
function peopleTeamSlotsCovered(people: PeopleOrgSignals): boolean {
  if (people.accountsRoleNames && people.accountsRoleNames.length > 0) {
    return isTeamCompositionSlotsMet(people.accountsRoleNames);
  }
  return peoplePolesCovered(people);
}

function isFlowOrgComplete(flowId: string, ctx: FlowReadinessContext): boolean {
  const s = ctx.signals;
  const people = ctx.people;
  switch (flowId) {
    case 'people-learn-roles':
      return (people?.activeStaffCount ?? s.staffAccountCount) >= 1;
    case 'people-create-account':
      return s.staffAccountCount >= 2;
    case 'people-cover-poles':
      return people ? peopleTeamSlotsCovered(people) : s.staffAccountCount >= 2;
    case 'people-verify-authority':
      return (
        (people?.superAdminCount ?? 0) >= 1 &&
        (people?.activeStaffWithRolesCount ?? 0) >= (people?.activeStaffCount ?? s.staffAccountCount) &&
        (people?.reportingGapCount ?? 0) === 0
      );
    case 'people-verify-sod':
      return (people?.sodViolationAccountCount ?? 0) === 0 && machineStepComplete('people', s);
    case 'catalog-create-product':
      return s.catalogSkuCount >= 1;
    case 'catalog-create-variant':
      return s.catalogSkuCount >= 1;
    case 'catalog-readiness':
      return machineStepComplete('catalog', s);
    case 'locations-create':
      return s.locationCount >= 1;
    case 'locations-classes':
      return machineStepComplete('locations', s);
    case 'buy-qualify-supplier':
      return s.qualifiedSupplierCount >= 1;
    case 'buy-create-po':
      return machineStepComplete('buy', s);
    case 'receive-po':
      return s.batchCount >= 1;
    case 'receive-verify-lot':
      return machineStepComplete('receive', s);
    case 'release-lot':
      return s.batchSaleableQty > 0;
    case 'release-verify-atp':
      return machineStepComplete('release', s);
    case 'sell-create-order':
      return s.shippedOrderCount >= 1;
    case 'sell-confirm-ship':
      return machineStepComplete('sell', s);
    case 'return-order':
      return s.shippedOrderCount >= 1;
    case 'return-assess-or-stop':
      return machineStepComplete('return', s);
    case 'money-inspect-coa':
    case 'money-close-period':
      return false;
    default:
      return false;
  }
}

export function resolveFlowOrgStatus(
  flowId: string,
  ctx: FlowReadinessContext,
  completedFlowIds: ReadonlySet<string>,
): GuidedFlowState {
  const flow = FLOW_BY_ID.get(flowId);
  if (!flow) {
    return { id: flowId, status: 'unavailable', blockedBy: null, evidence: {} };
  }
  if (flow.availability === 'unavailable') {
    return { id: flowId, status: 'unavailable', blockedBy: null, evidence: { availability: 'unavailable' } };
  }

  const entryBlocked = entryPackBlocked(flow.entryId, ctx);
  if (entryBlocked && !machineStepComplete(flow.entryId, ctx.signals)) {
    return {
      id: flowId,
      status: 'blocked',
      blockedBy: entryBlocked,
      evidence: { entryId: flow.entryId },
    };
  }

  if (!flowPrerequisitesMet(flowId, completedFlowIds)) {
    const missing = flow.prerequisites.find((p) => !completedFlowIds.has(p)) ?? null;
    return {
      id: flowId,
      status: 'blocked',
      blockedBy: missing,
      evidence: {},
    };
  }

  if (isFlowOrgComplete(flowId, ctx)) {
    return { id: flowId, status: 'complete', blockedBy: null, evidence: { complete: true } };
  }

  return { id: flowId, status: 'actionable', blockedBy: null, evidence: {} };
}

export function resolveAllFlowStates(
  visibleIds: readonly string[],
  ctx: FlowReadinessContext,
): GuidedFlowState[] {
  const completed = new Set<string>();
  const ordered = GUIDED_FLOWS.filter((f) => visibleIds.includes(f.id));
  const states: GuidedFlowState[] = [];

  for (const flow of ordered) {
    const state = resolveFlowOrgStatus(flow.id, ctx, completed);
    states.push(state);
    if (state.status === 'complete') completed.add(flow.id);
  }
  return states;
}

export function nextIncompleteFlow(
  visibleIds: readonly string[],
  ctx: FlowReadinessContext,
): string | null {
  const states = resolveAllFlowStates(visibleIds, ctx);
  for (const s of states) {
    if (s.status === 'actionable') return s.id;
    if (s.status === 'blocked') return s.id;
  }
  return null;
}

export function entryOrgComplete(entryId: MachineJourneyId, ctx: FlowReadinessContext): boolean {
  const entryFlows = flowsForEntry(entryId).filter((f) => f.availability !== 'unavailable');
  if (entryFlows.length === 0) return machineStepComplete(entryId, ctx.signals);
  const completed = new Set<string>();
  for (const flow of entryFlows) {
    const state = resolveFlowOrgStatus(flow.id, ctx, completed);
    if (state.status !== 'complete') return false;
    completed.add(flow.id);
  }
  return true;
}

/** Compute SoD violation count for active staff (excludes SUPER_ADMIN-only bundles). */
export function countSodViolationsForRoles(roleNamesList: readonly (readonly string[])[]): number {
  let count = 0;
  for (const roles of roleNamesList) {
    if (roles.includes('SUPER_ADMIN')) continue;
    const packs = packsForRoles(roles);
    if (sodViolations(packs).length > 0) count += 1;
  }
  return count;
}

export type JourneyLearningStatus = 'STARTED' | 'COMPLETED';

export type JourneyLearningProgressRow = {
  flowId: string;
  revision: number;
  status: JourneyLearningStatus;
  lastStepId: string | null;
};

/** Merge org state + personal learning for UI CTA precedence. */
export type FlowUiState =
  | 'blocked'
  | 'guide' // actionable + learning unseen
  | 'continue' // actionable + learning complete
  | 'discover' // org complete + learning unseen
  | 'done'; // both complete

export function resolveFlowUiState(
  orgStatus: GuidedFlowOrgStatus,
  learningStatus: JourneyLearningStatus | null,
): FlowUiState {
  if (orgStatus === 'blocked' || orgStatus === 'unavailable') return 'blocked';
  const learningDone = learningStatus === 'COMPLETED';
  const orgDone = orgStatus === 'complete';
  if (orgDone && learningDone) return 'done';
  if (orgDone && !learningDone) return 'discover';
  if (!orgDone && learningDone) return 'continue';
  return 'guide';
}

export function primaryCtaLabel(uiState: FlowUiState): string {
  switch (uiState) {
    case 'blocked':
      return 'Étape précédente requise';
    case 'guide':
      return 'Commencer avec le guide';
    case 'continue':
      return 'Continuer';
    case 'discover':
      return 'Découvrir ce fonctionnement';
    case 'done':
      return 'Terminé';
  }
}
